const BASE = import.meta.env.VITE_API_URL || "";

export function getToken() {
  return localStorage.getItem("calliq_token");
}

export function getStoredUser() {
  try {
    const raw = localStorage.getItem("calliq_user");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setAuth(token, user) {
  localStorage.setItem("calliq_token", token);
  localStorage.setItem("calliq_user", JSON.stringify(user));
}

export function clearAuth() {
  localStorage.removeItem("calliq_token");
  localStorage.removeItem("calliq_user");
}

// A random id this browser keeps, sent with every request so the sign-in history can tell
// "a device you've used before" from "a new device" (Salem 2026-10-01). It identifies the
// browser, not the person, and carries nothing else.
const DEVICE_KEY = "repiq_device_id";
let _deviceId = null;
export function deviceId() {
  if (_deviceId) return _deviceId;
  try {
    _deviceId = localStorage.getItem(DEVICE_KEY);
    if (!_deviceId) {
      const rnd = (window.crypto && window.crypto.getRandomValues)
        ? Array.from(window.crypto.getRandomValues(new Uint8Array(16)), (b) => b.toString(16).padStart(2, "0")).join("")
        : (Math.random().toString(36).slice(2) + Date.now().toString(36) + Math.random().toString(36).slice(2));
      _deviceId = "d-" + rnd.slice(0, 40);
      localStorage.setItem(DEVICE_KEY, _deviceId);
    }
  } catch {
    _deviceId = null;
  }
  return _deviceId;
}

// While an order form opened from a Cobra reconciliation is on screen, saves carry this so no order history is
// written (Salem 2026-11-02). The server honours it for an admin only.
let _historyOff = false;
export function setOrderHistoryOff(v) { _historyOff = !!v; }

async function request(method, path, body) {
  const headers = {};
  if (_historyOff) headers["X-Order-History"] = "off";
  const did = deviceId();
  if (did) headers["X-Device-Id"] = did;
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  let res;
  try {
    res = await fetch(BASE + path, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Network error — could not reach the server");
  }
  if (res.status === 401) {
    // On the login page a 401 means bad credentials — show the real reason. Elsewhere it
    // means the saved token is no longer valid, so treat it as an expired session.
    let detail = "";
    try {
      const d = await res.json();
      detail = d && d.detail ? (typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail)) : "";
    } catch { /* ignore */ }
    if (window.location.pathname.startsWith("/login")) {
      throw new Error(detail || "Invalid email or password");
    }
    clearAuth();
    window.location.href = "/login";
    throw new Error("Session expired — please sign in again");
  }
  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    try {
      const data = await res.json();
      if (data && data.detail) {
        msg = typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail);
      }
    } catch {
      /* ignore */
    }
    const err = new Error(msg);
    err.status = res.status;
    throw err;
  }
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return res;
}

// Multipart upload (FormData) — never set Content-Type so the browser adds the boundary.
async function upload(path, formData) {
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(BASE + path, { method: "POST", headers, body: formData });
  if (!res.ok) {
    let msg = `Upload failed (${res.status})`;
    try { const d = await res.json(); if (d && d.detail) msg = typeof d.detail === "string" ? d.detail : JSON.stringify(d.detail); } catch { /* ignore */ }
    const err = new Error(msg); err.status = res.status; throw err;
  }
  const ct = res.headers.get("content-type") || "";
  return ct.includes("application/json") ? res.json() : res;
}

export const api = {
  get: (path) => request("GET", path),
  post: (path, body) => request("POST", path, body),
  patch: (path, body) => request("PATCH", path, body),
  put: (path, body) => request("PUT", path, body),
  del: (path, body) => request("DELETE", path, body),
  delete: (path, body) => request("DELETE", path, body),
  upload,
  base: BASE,
};

// Fetch a binary resource (e.g. audio) with auth, returns an object URL or null.
export async function fetchBlobUrl(path) {
  const headers = {};
  const token = getToken();
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(BASE + path, { headers });
  if (!res.ok) {
    const err = new Error(`Failed to load (${res.status})`);
    err.status = res.status;
    throw err;
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export default api;
